import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI, InAppNotification } from '../services/api';
import { useTranslation } from 'react-i18next';
import './Notifications.css';

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['notifications', 'common']);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.list(filter === 'unread', 50, 0);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: InAppNotification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await notificationAPI.markAsRead(notification.id);
        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate to deep link if available
    if (notification.deep_link) {
      navigate(notification.deep_link);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationAPI.markAllAsRead();
      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (id: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering notification click

    try {
      await notificationAPI.delete(id);
      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== id));
      setTotal(prev => prev - 1);

      // Update unread count if the deleted notification was unread
      const deletedNotification = notifications.find(n => n.id === id);
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common:time.justNow');
    if (diffMins < 60) return t('common:time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('common:time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('common:time.daysAgo', { count: diffDays });

    const locale = i18n.language.startsWith('en') ? 'en-US' : 'pt-BR';
    return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  };

  const getDocumentName = (notification: InAppNotification): string | null => {
    // Try to extract document name from deep_link
    if (notification.deep_link && notification.deep_link.includes('/documents/')) {
      return t('notifications:dropdown.document');
    }
    return null;
  };

  return (
    <div className="notifications-page">
      <div className="notifications-container">
        <div className="notifications-header">
          <h1>{t('notifications:page.title')}</h1>
          <div className="notifications-header-actions">
            <div className="notifications-filter">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                {t('notifications:page.filterAll', { count: total })}
              </button>
              <button
                className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
                onClick={() => setFilter('unread')}
              >
                {t('notifications:page.filterUnread', { count: unreadCount })}
              </button>
            </div>
            {unreadCount > 0 && (
              <button className="btn btn-secondary" onClick={handleMarkAllAsRead}>
                {t('notifications:page.markAllRead')}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="notifications-loading">
            <div className="loading-spinner"></div>
            <p>{t('notifications:page.loading')}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <div className="empty-icon">🔔</div>
            <h2>{t('notifications:page.empty')}</h2>
            <p>
              {filter === 'unread'
                ? t('notifications:page.emptyUnread')
                : t('notifications:page.empty')}
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                {!notification.is_read && <span className="notification-unread-dot"></span>}
                <div className="notification-content">
                  <h3 className="notification-title">{notification.title}</h3>
                  {notification.body && (
                    <p className="notification-body">{notification.body}</p>
                  )}
                  <div className="notification-meta">
                    {getDocumentName(notification) && (
                      <span className="notification-document-name">{getDocumentName(notification)}</span>
                    )}
                    <span className="notification-time">{formatDate(notification.created_at)}</span>
                  </div>
                </div>
                <button
                  className="notification-delete"
                  onClick={(e) => handleDeleteNotification(notification.id, e)}
                  title={t('notifications:dropdown.delete')}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
